import React from 'react';
import './DriftingMoon.css';
import { useDarkMode } from '../contexts/DarkModeContext';
import moonLight from '../assets/moon-light.webp';
import moonLight2x from '../assets/moon-light@2x.webp';
import moonDark from '../assets/moon-dark.webp';
import moonDark2x from '../assets/moon-dark@2x.webp';

/**
 * The floating island (a real feature, toggled by the background-effects
 * preference). Calm version: a small badge parked in the bottom-right corner
 * with a slow drift, no physics, no rotation bounce and no ripples. It sits
 * under the page content (z-index 0, pointer-events none) and on phones
 * keeps clear of the bottom 88px so it never covers a primary CTA.
 *
 * Perf (PP): the badge ships as a 200px / 400px WebP (7 / 15 KB) instead of
 * the 1 MB 1089px PNG, with the theme's drop-shadow baked into the bitmap
 * so the drifting layer carries no filter. The wrapper itself is the fixed
 * element (128px, not a viewport-sized container), promoted only for the
 * transform drift, paused while the tab is hidden (body.is-hidden) and
 * static under prefers-reduced-motion.
 */
/* PP-09: a running CSS animation makes Blink recompute style on the main
 * thread every frame, even when the effect is compositor-only (measured on
 * /practice: 770 recalcs / 1.1 s per scroll with the drift on, 0 with it
 * off - the drift was the whole scroll-time style cost). The drift therefore
 * pauses under body.is-scrolling, which App.tsx toggles from one capture-phase
 * scroll listener (shared with the dashboard halo), and resumes from where it
 * stopped ~400 ms after the last scroll event. Invisible: a 6 px sway over 8 s. */
const DriftingMoon: React.FC = () => {
  const { enabled: dark } = useDarkMode();
  const src = dark ? moonDark : moonLight;
  const src2x = dark ? moonDark2x : moonLight2x;

  return (
    <div className="moon-wrapper moon-wrapper--calm" aria-hidden="true">
      <img
        className="moon-img"
        src={src}
        srcSet={`${src} 1x, ${src2x} 2x`}
        width={200}
        height={200}
        alt=""
        draggable={false}
        decoding="async"
        loading="lazy"
        fetchPriority="low"
      />
    </div>
  );
};

export default DriftingMoon;
